import axios, {
  type AxiosInstance,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from 'axios';
import { env } from './env';
import { ApiError, toApiError } from './errors';
import { setActingTenant, type ActingTenant } from './actingTenant';
import { appEvents } from './events';
import { session } from './session';

/**
 * Backend response envelope (zafroi-mvp-runtime):
 *   success === true  → { success: true,  data: <T>,    meta }
 *   success === false → { success: false, error: {...}, meta }
 */
export interface ApiEnvelope<T> {
  success: boolean;
  data?: T;
  error?: { code: string; message: string; details?: unknown };
  meta?: {
    requestId?: string;
    timestamp?: string;
    /** Set on PLATFORM_ADMIN cross-tenant requests (compliance addendum §A.6). */
    actingAsTenant?: ActingTenant;
  };
}

/**
 * Auth endpoints that should NOT trigger the auto-refresh-on-401 cycle
 * (otherwise we recurse on a failed login).
 */
const AUTH_PUBLIC_PATTERN =
  /\/api\/v1\/auth\/(login|refresh|forgot-password|reset-password|accept-invite|invite\/|mfa\/verify)/;

const instance: AxiosInstance = axios.create({
  baseURL: env.apiBaseUrl,
  timeout: 20_000,
  // withCredentials so the browser sends the cross-subdomain refresh
  // cookie set on .zafroi.com by /auth/login. Required for the cookie
  // path of /auth/refresh — and critically so that when engage-app (or
  // any other *.zafroi.com app) rotates the cookie, erp-app picks up
  // the rotated value on its next refresh instead of trying to refresh
  // with a stale localStorage token (which would trip the runtime's
  // refresh-token theft detection and revoke every session).
  withCredentials: true,
  headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
});

// ── Request: prefer Bearer JWT (human session), fall back to x-api-key
//    (integration / dev). Never send both — Bearer wins.
instance.interceptors.request.use((config) => {
  const active = session.current();
  if (active?.accessToken) {
    config.headers.set('Authorization', `Bearer ${active.accessToken}`);
  } else if (env.apiKey) {
    config.headers.set('x-api-key', env.apiKey);
  }
  return config;
});

// ── Response: capture acting-tenant; route SubscriptionExpired and
//    auth failures via appEvents; auto-refresh on 401.
let inflightRefresh: Promise<void> | null = null;

async function performRefresh(): Promise<void> {
  // Send the localStorage refresh token if we have one as a fallback for
  // older sessions, but prefer the .zafroi.com cookie (Phase 2 SSO) —
  // critically, when engage-app rotates the cookie, our next refresh
  // should use the rotated cookie value instead of our stale
  // localStorage token (which would trip the runtime's refresh-token
  // theft detection and revoke every session for the user). The cookie
  // is auto-attached because the axios instance has withCredentials:true.
  const refreshToken = session.getRefreshToken();
  const res = await axios.post<
    ApiEnvelope<{ accessToken: string; refreshToken: string; expiresAt: string }>
  >(
    `${env.apiBaseUrl}/api/v1/auth/refresh`,
    refreshToken ? { refreshToken } : {},
    {
      withCredentials: true,
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    },
  );
  const body = res.data;
  if (!body || body.success === false || !body.data) {
    throw new Error('Refresh failed');
  }
  session.updateAccessToken(body.data.accessToken, body.data.refreshToken, body.data.expiresAt);
}

instance.interceptors.response.use(
  (res) => {
    const body = res.data as ApiEnvelope<unknown> | undefined;
    if (body?.meta?.actingAsTenant) {
      setActingTenant(body.meta.actingAsTenant);
    }
    return res;
  },
  async (err) => {
    const apiError = toApiError(err);

    if (apiError.isSubscriptionExpired) {
      appEvents.emit('subscription-expired', {
        tenantId: (apiError.details as { tenantId?: string } | undefined)?.tenantId,
        expiredAt: (apiError.details as { expiredAt?: string } | undefined)?.expiredAt,
        requestId: apiError.requestId,
      });
      return Promise.reject(apiError);
    }

    const config = err.config as
      | (InternalAxiosRequestConfig & { _retried?: boolean })
      | undefined;
    const url = config?.url ?? '';
    const isAuthRoute = AUTH_PUBLIC_PATTERN.test(url);
    const canRetry = !!config && !config._retried && !isAuthRoute;
    const hasRefreshToken = !!session.getRefreshToken();

    if (apiError.isUnauthorized && canRetry && hasRefreshToken) {
      try {
        if (!inflightRefresh) {
          inflightRefresh = performRefresh().finally(() => {
            inflightRefresh = null;
          });
        }
        await inflightRefresh;
        config!._retried = true;
        return instance(config!);
      } catch {
        session.clear();
        appEvents.emit('auth-required', { reason: 'refresh-failed' });
        return Promise.reject(apiError);
      }
    }

    // If we already retried after a successful refresh and STILL got 401,
    // the refreshed token itself is unauthorized (e.g. BE secret rotated
    // mid-flight, user got remote-revoked). Don't loop — clear and route
    // to /login the same way a fresh-no-token call would.
    if (apiError.isUnauthorized && config?._retried && !isAuthRoute) {
      session.clear();
      appEvents.emit('auth-required', { reason: 'retry-still-unauthorized' });
      return Promise.reject(apiError);
    }

    // Unauthenticated and we have no creds path → tell the router.
    if (
      apiError.isUnauthorized &&
      !env.apiKey &&
      !hasRefreshToken &&
      !isAuthRoute
    ) {
      appEvents.emit('auth-required', { reason: 'no-session' });
    }

    return Promise.reject(apiError);
  },
);

async function unwrap<T>(p: Promise<{ data: ApiEnvelope<T> }>): Promise<T> {
  const res = await p;
  const body = res.data;
  if (body && body.success === false) {
    throw toApiError({
      isAxiosError: true,
      response: { status: 200, data: body },
      message: body.error?.message ?? 'Request failed',
    });
  }
  return body?.data as T;
}

export const http = {
  raw: instance,
  get: <T>(url: string, config?: AxiosRequestConfig) =>
    unwrap<T>(instance.get(url, config)),
  post: <T, B = unknown>(url: string, body?: B, config?: AxiosRequestConfig) =>
    unwrap<T>(instance.post(url, body, config)),
  put: <T, B = unknown>(url: string, body?: B, config?: AxiosRequestConfig) =>
    unwrap<T>(instance.put(url, body, config)),
  patch: <T, B = unknown>(url: string, body?: B, config?: AxiosRequestConfig) =>
    unwrap<T>(instance.patch(url, body, config)),
  delete: <T>(url: string, config?: AxiosRequestConfig) =>
    unwrap<T>(instance.delete(url, config)),
};

export { ApiError };
